import React from 'react';
import { useForm } from 'react-hook-form';
import { AlertCircle } from 'lucide-react';

const RiskForm = ({ onSubmit, loading }) => {
  const { register, handleSubmit, formState: { errors }, watch } = useForm({
    defaultValues: {
      age: 35,
      annualIncome: 1200000,
      monthlyExpenses: 60000,
      investmentHorizonYears: 20,
    }
  });

  const annualIncome = watch('annualIncome');
  const monthlyExpenses = watch('monthlyExpenses');
  const monthlyIncome = annualIncome ? Math.round(annualIncome / 12) : 0;
  const investableMonthly = monthlyIncome - monthlyExpenses;

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
        <div className="flex gap-3">
          <AlertCircle className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-blue-800">
            <p className="font-semibold mb-1">Quantitative Risk Assessment</p>
            <p>Your risk profile is calculated using a scientific 4-factor model based on your age, income stability, expenses, and investment horizon.</p>
          </div>
        </div>
      </div>

      {/* Age */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Age (years)</label>
        <input
          type="number"
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          placeholder="35"
          {...register('age', { 
            required: 'Age is required',
            min: { value: 18, message: 'Must be at least 18' },
            max: { value: 100, message: 'Must be 100 or less' }
          })}
        />
        {errors.age && <p className="text-red-500 text-xs mt-1">{errors.age.message}</p>}
      </div>

      {/* Annual Income */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Annual Gross Income (₹)</label>
        <input
          type="number"
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          placeholder="1200000"
          {...register('annualIncome', { 
            required: 'Annual income is required',
            min: { value: 100000, message: 'Must be at least ₹1,00,000' }
          })}
        />
        {errors.annualIncome && <p className="text-red-500 text-xs mt-1">{errors.annualIncome.message}</p>}
        <p className="text-xs text-gray-500 mt-1">Monthly Income: ₹{monthlyIncome.toLocaleString('en-IN')}</p>
      </div>

      {/* Monthly Essential Expenses */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Monthly Essential Expenses (₹)</label>
        <input
          type="number"
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          placeholder="60000"
          {...register('monthlyExpenses', { 
            required: 'Monthly expenses are required',
            min: { value: 0, message: 'Cannot be negative' }
          })}
        />
        {errors.monthlyExpenses && <p className="text-red-500 text-xs mt-1">{errors.monthlyExpenses.message}</p>}
        <p className="text-xs text-gray-500 mt-1">
          Expense Ratio: {monthlyIncome > 0 ? Math.round((monthlyExpenses / monthlyIncome) * 100) : 0}%
        </p>
      </div>

      {/* Investable Monthly Amount */}
      <div className="bg-green-50 border border-green-200 rounded-lg p-4">
        <p className="text-sm text-gray-600">Monthly Investable Capacity</p>
        <p className="text-2xl font-bold text-green-700">₹{investableMonthly.toLocaleString('en-IN')}</p>
        <p className="text-xs text-gray-500 mt-2">After deducting expenses. Emergency fund (6 months) will be maintained separately.</p>
      </div>

      {/* Investment Horizon */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Investment Horizon (years)</label>
        <input
          type="number"
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          placeholder="20"
          {...register('investmentHorizonYears', { 
            required: 'Investment horizon is required',
            min: { value: 1, message: 'Must be at least 1 year' },
            max: { value: 50, message: 'Must be 50 years or less' }
          })}
        />
        {errors.investmentHorizonYears && <p className="text-red-500 text-xs mt-1">{errors.investmentHorizonYears.message}</p>}
      </div>

      <button 
        type="submit" 
        className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-4 rounded-lg transition-colors duration-200"
        disabled={loading}
      >
        {loading ? 'Calculating Risk Profile...' : 'Calculate My Risk Profile'}
      </button>
    </form>
  );
};

export default RiskForm;
